// Floating Layers window — toggled by the topbar #topbar-layers icon.
//
// Renders sections of major scene objects (Bodies / Field Lines / Axes /
// Selection) with one checkbox per row. Window chrome (free drag, SE-corner
// resize, close, persisted position, click-to-front) comes from the shared
// WindowRegistry — the same primitive the Console / Reductions / Selection
// windows use, so there is no magnetic snap, edge-flip, or collapse. This
// module just (1) populates the body, (2) toggles the window via the topbar
// icon, and (3) re-renders on dataset/scene changes so the listing stays
// accurate.
//
// Visibility is wired entirely through typed store intents — this panel never
// imports @scene, mutates the scene graph, or writes params directly (the
// leaf-panel boundary in src/ui/README.md). Grids → SET_SHOW_GRID, field lines
// → SET_FIELDLINES_VISIBILITY, selection cube → SET_SELECTION_VISIBILITY,
// center+projections → SET_SELECTION_OVERLAY_VISIBILITY, axes vectors →
// SET_AXES_VECTORS_VISIBILITY, bodies → SET_BODY_VISIBILITY. Reading current
// state (params.*, entry.threeObject.visible) for checkbox sync is fine.
//
// Bodies are auto-discovered via the scene's ObjectRegistry, so adding a
// new sphere to scene.json surfaces it here without code changes.

import { store, params } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { WindowRegistry } from './windowManager';
import type { ViewerContext } from '@app/context';
import type { ObjectRegistry } from '@app/objectRegistry';

interface RowSpec {
    label: string;
    /** Current checked state — recomputed each render. */
    checked: boolean;
    /** Fired on toggle. */
    onChange: (next: boolean) => void;
    /** Optional getter for live-syncing via subscription. */
    getChecked?: () => boolean;
}

interface SectionSpec {
    title: string;
    rows: RowSpec[];
}

function getRegistry(viewer: ViewerContext): ObjectRegistry | null {
    return (viewer.objects.sceneRegistry as unknown as ObjectRegistry | null) ?? null;
}

function buildSections(viewer: ViewerContext): SectionSpec[] {
    const sections: SectionSpec[] = [];

    // --- Bodies (auto-listed from registry, spheres only) ---------------
    const registry = getRegistry(viewer);
    const bodies = (registry?.listGroup('physics') ?? []).filter((e) => e.type === 'sphere');
    if (bodies.length) {
        sections.push({
            title: 'Bodies',
            rows: bodies.map((entry) => ({
                label: entry.name,
                checked: entry.threeObject.visible,
                onChange: (next) => store.dispatch(INTENTS.SET_BODY_VISIBILITY, { id: entry.id, visible: next }),
                getChecked: () => entry.threeObject.visible,
            })),
        });
    }

    // --- Field Lines (master toggle, hidden when none exist) ------------
    const lineCount = viewer.objects.fieldlines.lines.length;
    if (lineCount > 0) {
        sections.push({
            title: 'Field Lines',
            rows: [{
                label: `All field lines (${lineCount})`,
                checked: !!params.showFieldLines,
                onChange: (next) => {
                    store.dispatch(INTENTS.SET_FIELDLINES_VISIBILITY, { visible: next });
                },
                getChecked: () => !!params.showFieldLines,
            }],
        });
    }

    // --- Axes -----------------------------------------------------------
    sections.push({
        title: 'Axes',
        rows: [
            {
                label: 'XY grid',
                checked: !!params.showGridXY,
                onChange: (next) => store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XY', value: next }),
                getChecked: () => !!params.showGridXY,
            },
            {
                label: 'XZ grid',
                checked: !!params.showGridXZ,
                onChange: (next) => store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XZ', value: next }),
                getChecked: () => !!params.showGridXZ,
            },
            {
                label: 'YZ grid',
                checked: !!params.showGridYZ,
                onChange: (next) => store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'YZ', value: next }),
                getChecked: () => !!params.showGridYZ,
            },
            {
                label: 'Axes vectors',
                checked: !!params.showAxesVectors,
                onChange: (next) => store.dispatch(INTENTS.SET_AXES_VECTORS_VISIBILITY, { visible: next }),
                getChecked: () => !!params.showAxesVectors,
            },
        ],
    });

    // --- Selection ------------------------------------------------------
    sections.push({
        title: 'Selection',
        rows: [
            {
                label: 'Selection cube',
                checked: !!params.showSelectionCube,
                onChange: (next) => store.dispatch(INTENTS.SET_SELECTION_VISIBILITY, { visible: next }),
                getChecked: () => !!params.showSelectionCube,
            },
            {
                label: 'Center + projections',
                checked: !!params.showSelection,
                onChange: (next) => store.dispatch(INTENTS.SET_SELECTION_OVERLAY_VISIBILITY, { visible: next }),
                getChecked: () => !!params.showSelection,
            },
        ],
    });

    return sections;
}

interface PaneRefs {
    host: HTMLDivElement;
    /** Live checkbox elements keyed by section-title + row-label for sync. */
    rowMap: Map<string, { input: HTMLInputElement; getChecked: () => boolean }>;
}

function renderInto(viewer: ViewerContext, refs: PaneRefs): void {
    refs.host.innerHTML = '';
    refs.rowMap.clear();
    const sections = buildSections(viewer);
    for (const section of sections) {
        const sectionEl = document.createElement('div');
        sectionEl.className = 'layers-section';

        const titleEl = document.createElement('h4');
        titleEl.className = 'layers-section-title';
        titleEl.textContent = section.title;
        sectionEl.appendChild(titleEl);

        for (const row of section.rows) {
            const rowEl = document.createElement('label');
            rowEl.className = 'layers-row';

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.checked = row.checked;
            input.addEventListener('change', () => {
                try { row.onChange(input.checked); } catch { /* swallow */ }
            });

            const text = document.createElement('span');
            text.className = 'layers-row-label';
            text.textContent = row.label;

            rowEl.appendChild(input);
            rowEl.appendChild(text);
            sectionEl.appendChild(rowEl);

            if (row.getChecked) {
                refs.rowMap.set(`${section.title}::${row.label}`, { input, getChecked: row.getChecked });
            }
        }
        refs.host.appendChild(sectionEl);
    }
}

export function initLayersPanel(viewer: ViewerContext, registry: WindowRegistry): () => void {
    const btn = document.getElementById('topbar-layers') as HTMLButtonElement | null;
    const pane = document.getElementById('layers-panel') as HTMLDivElement | null;
    const host = document.getElementById('layers-content') as HTMLDivElement | null;
    const closeBtn = document.getElementById('layers-close') as HTMLButtonElement | null;
    const header = pane?.querySelector('.layers-header') as HTMLElement | null;
    const resize = pane?.querySelector('.layers-resize') as HTMLElement | null;
    if (!btn || !pane || !host || !header) return () => { /* nothing wired */ };

    const refs: PaneRefs = { host, rowMap: new Map() };
    renderInto(viewer, refs);

    const syncBtn = (visible: boolean): void => {
        btn.classList.toggle('active', visible);
        btn.setAttribute('aria-expanded', visible ? 'true' : 'false');
    };

    // Register with the shared WindowRegistry — owns free drag (no snap),
    // SE-corner resize, close-button wiring, persisted position/size, and
    // click-to-front. Same chrome as the Console / Reductions windows.
    const handle = registry.register({
        id: 'layers',
        panel: pane,
        header,
        resize,
        closeBtn,
        minWidth: 200,
        minHeight: 160,
        onVisibilityChange: (visible) => {
            syncBtn(visible);
            // Refresh on open — the body list and field-line count can change
            // while the window is hidden.
            if (visible) renderInto(viewer, refs);
        },
    });
    syncBtn(handle.isVisible());

    const onBtnClick = (e: MouseEvent): void => {
        e.stopPropagation();
        handle.toggle();
    };
    btn.addEventListener('click', onBtnClick);

    // Live-sync checkbox values while visible — another UI surface (the Field
    // Lines pane, keyboard shortcuts) can flip the same params.
    const syncFromParams = (): void => {
        if (!handle.isVisible()) return;
        refs.rowMap.forEach(({ input, getChecked }) => {
            const next = getChecked();
            if (input.checked !== next) input.checked = next;
        });
    };
    const offParams = store.subscribe('params', syncFromParams);
    // Dataset switch or major rebuild → re-render so field-line count and
    // body list reflect the new scene state.
    const offRebuild = store.onIntent(INTENTS.UI_PANE_REBUILD_SECTION, () => {
        renderInto(viewer, refs);
    });

    return () => {
        btn.removeEventListener('click', onBtnClick);
        offParams();
        offRebuild();
        handle.dispose();
    };
}
